import { useEffect, useRef } from "react";
import { sendVideoMeetingHeartbeat } from "../utils/sendVideoMeetingHeartbeat";
import type {
  VideoRoomHeartbeatResponse,
  DeclaredVideoState,
} from "../videoMeeting.types";
import type { CallQualityMetrics } from "../utils/collectCallQualityMetrics";

const HEARTBEAT_INTERVAL_MS = 10_000; // 10 seconds

export type UseVideoMeetingHeartbeatArgs = {
  meetingId: string | null;
  userName: string | null;
  user: { id: number } | null;
  isEnabled?: boolean;
  onHeartbeat?: (response: VideoRoomHeartbeatResponse) => void;
  /** Synchronous getter for the current connection_quality value. Called
   *  once per heartbeat tick at the top of sendHeartbeat; the captured
   *  value is threaded into both getCallQualityMetrics and getDeclaredState
   *  so the per-sample (quality_metrics.connection_quality) and the
   *  top-level (declaredState.connection_quality) always agree on a tick.
   *  Without this single-capture point, the await between the two reads
   *  inside sendHeartbeat allows the underlying ref to flip mid-tick. */
  getConnectionQuality?: () => string;
  /** Async callback that collects WebRTC quality metrics for this tick.
   *  Receives the captured connection_quality value so the per-sample
   *  body matches the top-level. If provided, the result is included in
   *  the heartbeat POST body. */
  getCallQualityMetrics?: (capturedConnectionQuality: string) => Promise<CallQualityMetrics | null>;
  /** Synchronous getter for the user's declared session state
   *  (mic/camera/screen-share intent + connection quality). Receives the
   *  captured connection_quality value so it matches the per-sample. If
   *  provided, the four fields are sent at the top level of the heartbeat
   *  body and mirrored onto meeting_{mid}_{uid} by the backend. */
  getDeclaredState?: (capturedConnectionQuality: string) => DeclaredVideoState | null;
  /** Send an immediate heartbeat when the tab returns to foreground.
   *  Prevents the 30-second stale threshold from triggering transcription
   *  cleanup when Chrome throttles setInterval to 1/min in background tabs
   *  or iOS Safari suspends JS entirely. */
  resumeOnForeground?: boolean;
};

export function useVideoMeetingHeartbeat({
  meetingId,
  userName,
  user,
  isEnabled = true,
  onHeartbeat,
  getConnectionQuality,
  getCallQualityMetrics,
  getDeclaredState,
  resumeOnForeground = false,
}: UseVideoMeetingHeartbeatArgs) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const missedHeartbeatsRef = useRef(0);
  const resumedFromBackgroundRef = useRef(false);

  useEffect(() => {
    if (!isEnabled || !meetingId || !userName) {
      return;
    }

    const sendHeartbeat = async () => {
      try {
        // Capture connection_quality ONCE per tick. Both per-sample and
        // top-level read this same captured value rather than the live
        // ref — the await on getCallQualityMetrics (enumerateDevices +
        // getStats) is wide enough that the underlying ref could flip
        // between the two reads, producing per-sample/top-level
        // disagreement on a single tick.
        const capturedCq = getConnectionQuality?.() ?? 'good';

        const metrics = await getCallQualityMetrics?.(capturedCq) ?? null;

        // Inject connectivity context into metrics if present
        if (metrics) {
          metrics.missed_heartbeats = missedHeartbeatsRef.current;
          metrics.resumed_from_background = resumedFromBackgroundRef.current;
        }

        const declaredState = getDeclaredState?.(capturedCq) ?? null;

        // Read tab visibility at heartbeat construction time. Sliced into
        // the outbound_video_width distribution at audit time to disambiguate
        // "weak network / simulcast-gated / backgrounded tab" — see §10.
        const tabVisibility = document.visibilityState;

        const response = await sendVideoMeetingHeartbeat(
          meetingId,
          userName,
          user,
          metrics,
          declaredState,
          missedHeartbeatsRef.current,
          tabVisibility,
        );

        // Reset connectivity tracking after successful send
        missedHeartbeatsRef.current = 0;
        resumedFromBackgroundRef.current = false;

        onHeartbeat?.(response);
      } catch {
        missedHeartbeatsRef.current++;
      }
    };

    // Send immediately, then on interval
    sendHeartbeat();
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    // On foreground return, fire an immediate heartbeat so the backend
    // doesn't consider the user stale (30s threshold).
    const handleVisibilityChange = resumeOnForeground
      ? () => {
          if (document.visibilityState === 'visible') {
            resumedFromBackgroundRef.current = true;
            sendHeartbeat();
          }
        }
      : null;

    if (handleVisibilityChange) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (handleVisibilityChange) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [isEnabled, meetingId, userName, user, onHeartbeat, getConnectionQuality, getCallQualityMetrics, getDeclaredState, resumeOnForeground]);
}
